import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Palette, LayoutTemplate, ToggleLeft, Handshake, Upload, RotateCcw, LogIn, Laptop } from "lucide-react";
import LoginPageSettings from "../components/builder/LoginPageSettings";
import ThemeGallery from "../components/builder/ThemeGallery";
import AppsBuilder from "../components/builder/AppsBuilder";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { BuilderPreview, PREVIEW_VARIANTS } from "../components/builder/BuilderPreview";

const TABS = [
  { k: "branding", label: "Identité visuelle", icon: Palette },
  { k: "welcome", label: "Écran d'accueil & textes", icon: LayoutTemplate },
  { k: "login", label: "Connexion", icon: LogIn },
  { k: "apps", label: "Applications", icon: Laptop },
  { k: "features", label: "Fonctions de l'app", icon: ToggleLeft },
  { k: "partners", label: "Panel partenaire", icon: Handshake },
];

const COLOR_FIELDS = [
  ["primary", "Couleur principale"], ["secondary", "Couleur secondaire"], ["background", "Fond"], ["accent", "Accent"],
];

export default function SocieteBuilderPage() {
  const { societe_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [config, setConfig] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [tab, setTab] = useState("branding");
  const [variant, setVariant] = useState("trainer");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [slug, setSlugState] = useState("");
  const setSlug = (v) => { setSlugState(v); setDirty(true); };
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([api.get(`/societes/${societe_id}/builder`), api.get("/builder/registry")]);
      setConfig(c.data.config);
      setSlugState(c.data.config.slug || "");
      setRegistry(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    // eslint-disable-next-line
  }, [societe_id]);
  useEffect(() => { load(); }, [load]);

  const setBranding = (k, v) => { setConfig((c) => ({ ...c, branding: { ...c.branding, [k]: v } })); setDirty(true); };
  const setFeature = (k, v) => { setConfig((c) => ({ ...c, features: { ...c.features, [k]: v } })); setDirty(true); };
  const setPartner = (t, k, v) => { setConfig((c) => ({ ...c, partner_access: { ...c.partner_access, [t]: { ...(c.partner_access?.[t] || {}), [k]: v } } })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/societes/${societe_id}/builder`, { branding: config.branding, features: config.features, partner_access: config.partner_access, slug });
      setConfig(r.data.config);
      setSlugState(r.data.config.slug || "");
      setDirty(false);
      toast.success("Builder enregistré — appliqué à la page de connexion, au panel et aux apps de la société");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/societes/${societe_id}/builder/asset/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      const field = { logo: "logo_url", cover: "cover_url", logo_light: "logo_light_url" }[kind];
      setConfig((c) => ({ ...c, branding: { ...c.branding, [field]: url } }));
      toast.success(kind === "cover" ? "Visuel mis à jour" : "Logo mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const resetBranding = () => {
    setConfig((c) => ({ ...c, branding: { ...registry.default_branding, app_name: c.societe_name || "", logo_url: "" } }));
    setDirty(true);
  };

  if (!config || !registry) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#f7c935]" /></div>;

  const groups = [...new Set(registry.features.map((f) => f.group))];

  return (
    <div className="min-h-screen" data-testid="societe-builder-page">
      <div className="sticky top-0 z-20 bg-[#0b0811]/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate("/admin/societes")} className="text-slate-400" data-testid="builder-back"><ArrowLeft className="w-4 h-4 mr-1" /> Sociétés</Button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Builder par société</div>
          <div className="text-lg font-black text-white truncate" data-testid="builder-societe-name">{config.societe_name}</div>
        </div>
        {dirty && <span className="text-[10px] text-amber-400 font-bold" data-testid="builder-dirty">Modifications non enregistrées</span>}
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="builder-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-5 p-5">
        <div className="col-span-12 xl:col-span-5 space-y-4">
          <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={"builder-tab-" + t.k}
                className={"flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold border-b-2 whitespace-nowrap " + (tab === t.k ? "border-[#f7c935] text-[#f7c935]" : "border-transparent text-slate-400 hover:text-white")}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "branding" && (
            <div className="space-y-4" data-testid="builder-panel-branding">
              <ThemeGallery api={api} societeId={societe_id} config={config} onApplied={(cfg) => { setConfig(cfg); setSlugState(cfg.slug || ""); setDirty(false); }} />
              <Field label="Nom d'affichage de l'app"><Input value={config.branding.app_name || ""} onChange={(e) => setBranding("app_name", e.target.value)} className="bg-[#0b0811] border-white/10 text-white" data-testid="builder-app-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                {COLOR_FIELDS.map(([k, label]) => (
                  <Field key={k} label={label}>
                    <div className="flex items-center gap-2">
                      <input type="color" value={config.branding[k] || "#000000"} onChange={(e) => setBranding(k, e.target.value)} className="w-9 h-9 rounded border border-white/10 bg-transparent" data-testid={"builder-color-" + k} />
                      <Input value={config.branding[k] || ""} onChange={(e) => setBranding(k, e.target.value)} className="bg-[#0b0811] border-white/10 text-white font-mono text-xs" />
                    </div>
                  </Field>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Logo">
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset("logo", e.target.files?.[0])} data-testid="builder-logo-input" />
                  <div className="flex items-center gap-2">
                    {config.branding.logo_url ? <img src={config.branding.logo_url} alt="logo" className="h-9 max-w-[110px] object-contain rounded bg-white/5 p-1" /> : <div className="h-9 w-20 rounded bg-white/5 border border-dashed border-white/10" />}
                    <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} className="border-white/10 text-slate-300 text-xs" data-testid="builder-logo-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Changer</Button>
                  </div>
                </Field>
                <Field label="Visuel d'accueil (connexion)">
                  <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset("cover", e.target.files?.[0])} data-testid="builder-cover-input" />
                  <div className="flex items-center gap-2">
                    {config.branding.cover_url ? <img src={config.branding.cover_url} alt="cover" className="h-9 w-16 object-cover rounded" /> : <div className="h-9 w-16 rounded bg-white/5 border border-dashed border-white/10" />}
                    <Button variant="outline" size="sm" onClick={() => coverRef.current?.click()} className="border-white/10 text-slate-300 text-xs" data-testid="builder-cover-btn"><Upload className="w-3.5 h-3.5 mr-1" /> Changer</Button>
                  </div>
                </Field>
              </div>
              <Button variant="ghost" size="sm" onClick={resetBranding} className="text-slate-400 text-xs" data-testid="builder-reset-branding"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Réinitialiser l'identité visuelle</Button>
            </div>
          )}

          {tab === "apps" && <AppsBuilder api={api} societeId={societe_id} config={config} setBranding={setBranding} />}
          {tab === "login" && <LoginPageSettings config={config} slug={slug} setSlug={setSlug} setBranding={setBranding} uploadAsset={uploadAsset} />}

          {tab === "welcome" && (
            <div className="space-y-4" data-testid="builder-panel-welcome">
              <Field label="Titre de bienvenue"><Input value={config.branding.welcome_title || ""} onChange={(e) => setBranding("welcome_title", e.target.value)} className="bg-[#0b0811] border-white/10 text-white" data-testid="builder-welcome-title" /></Field>
              <Field label="Texte de bienvenue"><Textarea rows={3} value={config.branding.welcome_text || ""} onChange={(e) => setBranding("welcome_text", e.target.value)} className="bg-[#0b0811] border-white/10 text-white" data-testid="builder-welcome-text" /></Field>
              <Field label="Accroche de l'écran de connexion"><Input value={config.branding.login_tagline || ""} onChange={(e) => setBranding("login_tagline", e.target.value)} className="bg-[#0b0811] border-white/10 text-white" data-testid="builder-login-tagline" /></Field>
              <Field label="Pied de page (mentions, contact)"><Input value={config.branding.footer_text || ""} onChange={(e) => setBranding("footer_text", e.target.value)} className="bg-[#0b0811] border-white/10 text-white" data-testid="builder-footer-text" /></Field>
            </div>
          )}

          {tab === "features" && (
            <div className="space-y-5" data-testid="builder-panel-features">
              <p className="text-xs text-slate-500">Les fonctions désactivées disparaissent de l'app formateur et de l'espace stagiaire de cette société. De nouvelles fonctions pourront être ajoutées ici au fil des mises à jour.</p>
              {groups.map((g) => (
                <div key={g}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">{g}</div>
                  <div className="space-y-2">
                    {registry.features.filter((f) => f.group === g).map((f) => (
                      <label key={f.key} className="flex items-center justify-between gap-3 bg-[#14101c] border border-white/5 rounded-xl px-4 py-3 cursor-pointer hover:border-white/15" data-testid={"builder-feature-" + f.key}>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-white">{f.label}</div>
                          <div className="text-[11px] text-slate-500">{f.desc}</div>
                        </div>
                        <Switch checked={!!config.features[f.key]} onCheckedChange={(v) => setFeature(f.key, v)} data-testid={"builder-feature-switch-" + f.key} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "partners" && (
            <div className="space-y-5" data-testid="builder-panel-partners">
              <p className="text-xs text-slate-500">Modules accessibles par défaut à chaque type de partenaire de cette société. Chaque partenaire peut ensuite être ajusté individuellement depuis <button className="underline text-[#f7c935]" onClick={() => navigate("/admin/partenaires")}>Partenaires</button>.</p>
              {registry.partner_types.map((t) => (
                <div key={t.key} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={"builder-partner-type-" + t.key}>
                  <div className="font-black text-white text-sm">{t.label}</div>
                  <div className="text-[11px] text-slate-500 mb-3">{t.desc}</div>
                  <div className="space-y-2">
                    {(registry.partner_modules[t.key] || []).map((m) => (
                      <label key={m.key} className="flex items-center justify-between gap-3 text-xs cursor-pointer">
                        <div><span className="text-slate-200 font-semibold">{m.label}</span><span className="text-slate-500 ml-2">{m.desc}</span></div>
                        <Switch checked={!!(config.partner_access?.[t.key] || {})[m.key]} onCheckedChange={(v) => setPartner(t.key, m.key, v)} data-testid={`builder-partner-${t.key}-${m.key}`} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 xl:col-span-7">
          <div className="sticky top-20 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Aperçu en direct</div>
              <div className="flex gap-1 bg-[#14101c] border border-white/10 rounded-lg p-1">
                {PREVIEW_VARIANTS.map((v) => (
                  <button key={v.key} onClick={() => setVariant(v.key)} data-testid={"builder-preview-" + v.key + "-btn"}
                    className={"flex items-center gap-1 px-2.5 py-1.5 rounded text-[11px] font-bold " + (variant === v.key ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:text-white")}>
                    <v.icon className="w-3 h-3" /> {v.label}
                  </button>
                ))}
              </div>
            </div>
            <BuilderPreview config={config} variant={variant} registry={registry} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">{label}</div>
      {children}
    </div>
  );
}
